import React, { useState } from "react";
import "./Scrapbook.scss";
import i18n from "i18n/config";

interface ScrapbookEntry {
  title?: string;
  description?: string;
  image: string;
}

interface ScrapbookModalProps {
  onClose?: () => void;
}

const formatDescriptionHtml = (description?: string): string =>
  (description || "").replace(/\n/g, "<br />");

const ScrapbookModal: React.FC<ScrapbookModalProps> = ({ onClose }) => {
  const rawEntries = i18n.getResourceBundle(i18n.language, "scrapbook");
  const entries: ScrapbookEntry[] = Array.isArray(rawEntries) ? rawEntries : [];

  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const nextSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev + 1) % entries.length);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const prevSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev - 1 + entries.length) % entries.length);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const goToSlide = (index: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide(index);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 75) {
      nextSlide();
    }

    if (touchStart - touchEnd < -75) {
      prevSlide();
    }
  };

  const currentEntry = entries[currentSlide];
  const mediaSrc = currentEntry?.image || "";
  const isMp4 = /\.mp4($|\?)/i.test(mediaSrc);

  if (!entries.length || !currentEntry) {
    return (
      <div className="scrapbook-modal">
        <div className="scrapbook-modal__frame">
          {onClose && (
            <button
              className="scrapbook-modal__close"
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!entries.length || !currentEntry) {
    return (
      <div className="scrapbook-modal">
        <div className="scrapbook-modal__frame">
          {onClose && (
            <button
              className="scrapbook-modal__close"
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="scrapbook-modal">
      <div className="scrapbook-modal__frame">
        {onClose && (
          <button
            className="scrapbook-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        )}

        <button
          className="scrapbook-modal__nav scrapbook-modal__nav--prev"
          onClick={prevSlide}
          aria-label="Previous slide"
          disabled={isAnimating}
        >
          ‹
        </button>

        <button
          className="scrapbook-modal__nav scrapbook-modal__nav--next"
          onClick={nextSlide}
          aria-label="Next slide"
          disabled={isAnimating}
        >
          ›
        </button>

        <div className="scrapbook-modal__content">
          <div
            className="scrapbook-modal__slider"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="scrapbook-modal__slide" key={currentSlide}>
              <div className="scrapbook-modal__image-container">
                {isMp4 ? (
                  <video
                    src={mediaSrc}
                    className="scrapbook-modal__image"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    aria-label={currentEntry.title || "Scrapbook video"}
                  />
                ) : (
                  <img
                    src={mediaSrc || "Scrapbook entry"}
                    alt={currentEntry.title || "Scrapbook entry"}
                    className="scrapbook-modal__image"
                    onError={(e) => {
                      // Fallback to placeholder if image doesn't load
                      (e.target as HTMLImageElement).src =
                        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="320" height="400"%3E%3Crect fill="%23e0e0e0" width="320" height="400"/%3E%3Ctext fill="%239a9a9a" font-family="Arial" font-size="16" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3EImage Coming Soon%3C/text%3E%3C/svg%3E';
                    }}
                  />
                )}
              </div>

              <div className="scrapbook-modal__caption">
                <p
                  className="scrapbook-modal__description"
                  dangerouslySetInnerHTML={{
                    __html: formatDescriptionHtml(currentEntry.description),
                  }}
                ></p>
              </div>
            </div>
          </div>

          <div className="scrapbook-modal__dots">
            {entries.map((_, index) => (
              <button
                key={index}
                className={`scrapbook-modal__dot ${
                  index === currentSlide ? "scrapbook-modal__dot--active" : ""
                }`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          <div className="scrapbook-modal__counter">
            {currentSlide + 1} / {entries.length}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScrapbookModal;
